import { z } from 'zod'
import type { SPICE_LEVELS } from '../constants'
import { localizedTextSchema, moneySchema, uuidSchema } from './common'

/** FR-CAT-2/3 — the catalog shape shared by kiosk, QR, website and admin. */

export const spiceLevelSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
]) as z.ZodType<(typeof SPICE_LEVELS)[number]>

export const modifierSchema = z.object({
  id: uuidSchema,
  name: localizedTextSchema,
  priceDelta: z.number().int(),
  isDefault: z.boolean().default(false),
})

export const modifierGroupSchema = z.object({
  id: uuidSchema,
  name: localizedTextSchema,
  /** `single` renders as radio buttons, `multi` as checkboxes. */
  selection: z.enum(['single', 'multi']),
  required: z.boolean().default(false),
  maxSelections: z.number().int().min(1).nullable().default(null),
  options: z.array(modifierSchema),
})

export const variantSchema = z.object({
  id: uuidSchema,
  name: localizedTextSchema,
  price: moneySchema,
  isDefault: z.boolean().default(false),
  isAvailable: z.boolean().default(true),
})

export const productSchema = z.object({
  id: uuidSchema,
  categoryId: uuidSchema,
  name: localizedTextSchema,
  description: localizedTextSchema.nullable(),
  imageUrl: z.string().nullable(),
  price: moneySchema,
  calories: z.number().int().min(0).nullable(),
  isVeg: z.boolean(),
  spiceLevel: spiceLevelSchema,
  isBestseller: z.boolean(),
  isAvailable: z.boolean(),
  sortOrder: z.number().int(),
  variants: z.array(variantSchema).default([]),
  modifierGroups: z.array(modifierGroupSchema).default([]),
})
export type Product = z.infer<typeof productSchema>

export const categorySchema = z.object({
  id: uuidSchema,
  name: localizedTextSchema,
  slug: z.string(),
  imageUrl: z.string().nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
})
export type Category = z.infer<typeof categorySchema>

/** FR-CAT-4 — one response bootstraps an entire ordering surface. */
export const menuResponseSchema = z.object({
  branch: z.object({
    id: uuidSchema,
    code: z.string(),
    name: localizedTextSchema,
    vatRateBp: z.number().int(),
    vatMode: z.enum(['exclusive', 'inclusive']),
    currency: z.literal('BDT'),
  }),
  categories: z.array(categorySchema),
  products: z.array(productSchema),
  /** Cache-busting stamp; clients refetch when it changes. */
  version: z.string(),
  generatedAt: z.number().int(),
})
export type MenuResponse = z.infer<typeof menuResponseSchema>
